import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ReportTemplateLibrary = ({ category, onGenerateReport, selectedFormat }) => {
  const [generatingId, setGeneratingId] = useState(null);

  const templates = {
    financial: [
      {
        id: 'fin-1',
        name: 'Daily Revenue Summary',
        description: 'Complete revenue breakdown by payment method and merchant',
        icon: 'TrendingUp',
        fields: ['Transaction Volume', 'Revenue', 'Fees', 'Net Settlement'],
        lastGenerated: '2 hours ago',
        frequency: 'Daily'
      },
      {
        id: 'fin-2',
        name: 'Settlement Reconciliation',
        description: 'Automated settlement matching with discrepancy detection',
        icon: 'FileCheck',
        fields: ['Settlement ID', 'Expected Amount', 'Actual Amount', 'Variance'],
        lastGenerated: '5 hours ago',
        frequency: 'Daily'
      },
      {
        id: 'fin-3',
        name: 'Monthly Financial Statement',
        description: 'Comprehensive P&L statement with trend analysis',
        icon: 'BarChart3',
        fields: ['Revenue', 'Costs', 'Profit Margin', 'Growth Rate'],
        lastGenerated: '1 day ago',
        frequency: 'Monthly'
      }
    ],
    fraud: [
      {
        id: 'fraud-1',
        name: 'Fraud Pattern Analysis',
        description: 'ML-detected suspicious transaction patterns',
        icon: 'AlertTriangle',
        fields: ['Risk Score', 'Pattern Type', 'Affected Transactions', 'Loss Amount'],
        lastGenerated: '1 hour ago',
        frequency: 'Real-time'
      },
      {
        id: 'fraud-2',
        name: 'Chargeback Report',
        description: 'Chargeback trends and merchant impact analysis',
        icon: 'RefreshCcw',
        fields: ['Chargeback Count', 'Dispute Rate', 'Win Rate', 'Financial Impact'],
        lastGenerated: '3 hours ago',
        frequency: 'Weekly'
      },
      {
        id: 'fraud-3',
        name: 'Risk Score Distribution',
        description: 'Transaction risk scoring across all merchants',
        icon: 'Shield',
        fields: ['Low Risk', 'Medium Risk', 'High Risk', 'Blocked'],
        lastGenerated: '30 minutes ago',
        frequency: 'Hourly'
      }
    ],
    merchant: [
      {
        id: 'merch-1',
        name: 'Merchant Performance Summary',
        description: 'Top performing merchants by revenue and volume',
        icon: 'Award',
        fields: ['Merchant Name', 'Transaction Count', 'Revenue', 'Success Rate'],
        lastGenerated: '4 hours ago',
        frequency: 'Daily'
      },
      {
        id: 'merch-2',
        name: 'Onboarding Funnel Analysis',
        description: 'Merchant onboarding conversion and drop-off rates',
        icon: 'Users',
        fields: ['Stage', 'Conversion Rate', 'Avg Time', 'Drop-off Reasons'],
        lastGenerated: '1 day ago',
        frequency: 'Weekly'
      },
      {
        id: 'merch-3',
        name: 'Merchant Activity Heatmap',
        description: 'Transaction activity patterns by time and location',
        icon: 'Map',
        fields: ['Time Slot', 'Region', 'Transaction Count', 'Peak Hours'],
        lastGenerated: '6 hours ago',
        frequency: 'Daily'
      }
    ],
    compliance: [
      {
        id: 'comp-1',
        name: 'Regulatory Compliance Report',
        description: 'PCI-DSS and local regulation compliance status',
        icon: 'FileText',
        fields: ['Compliance Area', 'Status', 'Last Audit', 'Next Review'],
        lastGenerated: '2 days ago',
        frequency: 'Monthly'
      },
      {
        id: 'comp-2',
        name: 'Audit Trail Export',
        description: 'Complete user activity and system changes log',
        icon: 'History',
        fields: ['User', 'Action', 'Timestamp', 'IP Address', 'Changes'],
        lastGenerated: '12 hours ago',
        frequency: 'On-demand'
      },
      {
        id: 'comp-3',
        name: 'Tax Documentation',
        description: 'Transaction-level tax calculation and reporting',
        icon: 'Receipt',
        fields: ['Transaction ID', 'Tax Amount', 'Tax Rate', 'Category'],
        lastGenerated: '1 day ago',
        frequency: 'Monthly'
      }
    ]
  };

  const currentTemplates = templates?.[category] || [];

  const handleGenerate = async (template) => {
    setGeneratingId(template?.id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    onGenerateReport(template);
    setGeneratingId(null);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="BookOpen" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">
            Report Template Library
          </h3>
        </div>
        <span className="text-sm text-muted-foreground">
          {currentTemplates?.length} templates available
        </span>
      </div>

      <div className="space-y-4">
        {currentTemplates?.map((template) => (
          <div
            key={template?.id}
            className="p-4 border border-border rounded-lg hover:border-primary/50 transition-all duration-200"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name={template?.icon} size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-semibold text-foreground mb-1">
                    {template?.name}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {template?.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {template?.fields?.map((field, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-muted text-xs text-foreground rounded"
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      Last: {template?.lastGenerated}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      {template?.frequency}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                iconName="Play"
                iconPosition="left"
                loading={generatingId === template?.id}
                onClick={() => handleGenerate(template)}
              >
                Generate
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportTemplateLibrary;